import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Skeleton,
  Empty,
  Button,
  Popconfirm,
  Alert,
  Tooltip,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  BarChartOutlined,
  PictureOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { TeacherTasksAPI } from "../../../../api/teacherTasksAPI";
import { StatusTag, BrandThemeProvider } from "../../../../ui";
import styles from "./TaskDetailModal.module.scss";

/**
 * TaskDetailModal — vista de detalle de una tarea (mockup modal v2-pro).
 *
 * Carga la tarea por id y muestra: titulo + estado, clase, pasos (escenas) con
 * su consigna, cuestionario final y acciones (Resultados / Editar / Borrar).
 *
 * Reglas de borrado (identicas al backend):
 *  - draft     -> deleteDraftTask
 *  - archived  -> purgeArchivedTask (definitivo)
 *  - active    -> bloqueado (archivar primero)
 *
 * Props:
 *  - open, onClose
 *  - accessToken, taskId
 *  - onChanged()        -> recargar listado tras borrar
 *  - onEdit(task)       -> abrir edicion (solo draft); opcional
 *  - onOpenResults(id)  -> abrir resultados; opcional
 */
export function TaskDetailModal({
  open,
  onClose,
  accessToken,
  taskId,
  onChanged,
  onEdit,
  onOpenResults,
}) {
  const { t } = useTranslation("tasks");
  const api = useMemo(() => new TeacherTasksAPI(), []);

  const [loading, setLoading] = useState(false);
  const [task, setTask] = useState(null);
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    let ignore = false;
    if (!open || !taskId) return undefined;

    (async () => {
      try {
        setLoading(true);
        setError("");
        setDeleteError("");
        const { task: data } = await api.getTaskById(accessToken, taskId);
        if (!ignore) setTask(data || null);
      } catch (e) {
        if (!ignore) setError(e?.message || t("detail.loadError"));
      } finally {
        if (!ignore) setLoading(false);
      }
    })();

    return () => {
      ignore = true;
    };
  }, [open, taskId, accessToken, api, t]);

  const status = task?.status || "draft";
  const isArchived = status === "archived";
  const isActive = status === "active";

  const steps = Array.isArray(task?.steps)
    ? [...task.steps].sort((a, b) => (a.order || 0) - (b.order || 0))
    : [];

  const classnumber =
    task?.classnumber || task?.classroom?.classnumber || task?.classroom?.name;

  const finalQuizName =
    task?.finalQuizName ||
    task?.finalQuiz?.name ||
    task?.finalQuiz?.title ||
    (task?.finalQuizRequired ? t("detail.quizYes") : t("detail.quizNone"));

  const handleDelete = async () => {
    if (!task) return;
    try {
      setDeleting(true);
      setDeleteError("");
      if (isActive) throw new Error(t("list.deleteActiveError"));
      if (isArchived) {
        await api.purgeArchivedTask(accessToken, task._id);
      } else {
        await api.deleteDraftTask(accessToken, task._id);
      }
      await onChanged?.();
      onClose?.();
    } catch (e) {
      setDeleteError(e?.message || t("list.deleteError"));
    } finally {
      setDeleting(false);
    }
  };

  const handleActivate = async () => {
    if (!task) return;
    try {
      setToggling(true);
      setDeleteError("");
      await api.activateTask(accessToken, task._id);
      await onChanged?.();
      onClose?.();
    } catch (e) {
      setDeleteError(e?.message || t("list.activateError"));
    } finally {
      setToggling(false);
    }
  };

  const handleReactivate = async () => {
    if (!task) return;
    try {
      setToggling(true);
      setDeleteError("");
      await api.reactivateTask(accessToken, task._id); // archived -> draft
      await onChanged?.();
      onClose?.();
    } catch (e) {
      setDeleteError(e?.message || t("list.reactivateError"));
    } finally {
      setToggling(false);
    }
  };

  const handleDeactivate = async () => {
    if (!task) return;
    try {
      setToggling(true);
      setDeleteError("");
      await api.deactivateTask(accessToken, task._id); // backend -> 'archived'
      await onChanged?.();
      onClose?.();
    } catch (e) {
      setDeleteError(e?.message || t("list.archiveError"));
    } finally {
      setToggling(false);
    }
  };

  // Titulo de cada paso: usa la escena poblada si existe; si no, "Paso N".
  const stepTitle = (step, idx) =>
    step?.sceneTitle ||
    step?.scene?.title ||
    t("detail.stepFallback", { n: idx + 1 });

  const deleteLabel = isArchived
    ? t("list.deleteButton")
    : t("list.deleteDraftButton");

  return (
    <BrandThemeProvider>
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        width={560}
        centered
        className={styles.modal}
        styles={{ body: { padding: 0 } }}
        destroyOnHidden
      >
        {loading ? (
          <div className={styles.pad}>
            <Skeleton active paragraph={{ rows: 6 }} />
          </div>
        ) : error ? (
          <div className={styles.pad}>
            <Alert type="error" showIcon message={error} />
          </div>
        ) : !task ? (
          <div className={styles.pad}>
            <Empty description={t("detail.notFound")} />
          </div>
        ) : (
          <>
            {/* Cabecera */}
            <div className={styles.header}>
              <div className={styles.titleRow}>
                <h3 className={styles.title}>{task.title}</h3>
                <StatusTag status={status} />
              </div>
              <div className={styles.meta}>
                <span className={styles.metaLabel}>{t("detail.class")}</span>
                <span className={styles.metaValue}>{classnumber || "-"}</span>
              </div>
            </div>

            {/* Pasos */}
            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                {t("detail.stepsTitle", { count: steps.length })}
              </div>

              {steps.length === 0 ? (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={t("detail.noSteps")}
                />
              ) : (
                <ol className={styles.steps}>
                  {steps.map((step, idx) => {
                    const banner =
                      step?.sceneBanner ||
                      step?.scene?.bannertopUrl ||
                      step?.scene?.bannerTopUrl ||
                      null;
                    return (
                      <li className={styles.step} key={step._id || idx}>
                        <div className={styles.thumb}>
                          {banner ? (
                            <img src={banner} alt="" />
                          ) : (
                            <>
                              <PictureOutlined className={styles.thumbIcon} />
                              <span className={styles.thumbNum}>{idx + 1}</span>
                            </>
                          )}
                        </div>
                        <div className={styles.stepBody}>
                          <div className={styles.stepName}>
                            {stepTitle(step, idx)}
                          </div>
                          {step.instruction ? (
                            <div className={styles.stepDesc}>
                              {step.instruction}
                            </div>
                          ) : null}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>

            {/* Cuestionario final */}
            <div className={styles.quizRow}>
              <span className={styles.metaLabel}>{t("detail.finalQuiz")}</span>
              <span className={styles.metaValue}>{finalQuizName}</span>
            </div>

            {deleteError ? (
              <div className={styles.pad}>
                <Alert type="error" showIcon message={deleteError} />
              </div>
            ) : null}

            {/* Acciones */}
            <div className={styles.footer}>
              {onOpenResults ? (
                <Button
                  icon={<BarChartOutlined />}
                  onClick={() => onOpenResults(task._id)}
                >
                  {t("detail.results")}
                </Button>
              ) : (
                <span />
              )}

              <div className={styles.footerRight}>
                {isActive ? (
                  <Popconfirm
                    title={t("list.deactivateTitle")}
                    description={t("list.deactivateNote")}
                    okText={t("list.deactivateButton")}
                    okButtonProps={{ loading: toggling }}
                    cancelText={t("list.cancelButton")}
                    onConfirm={handleDeactivate}
                  >
                    <Button icon={<PauseCircleOutlined />} loading={toggling}>
                      {t("detail.deactivate")}
                    </Button>
                  </Popconfirm>
                ) : (
                  <Button
                    icon={<PlayCircleOutlined />}
                    loading={toggling}
                    onClick={isArchived ? handleReactivate : handleActivate}
                  >
                    {isArchived ? t("detail.reactivate") : t("detail.activate")}
                  </Button>
                )}

                <Tooltip
                  title={
                    isActive
                      ? t("detail.editOnlyInactive")
                      : isArchived
                        ? t("detail.editOnlyDraft")
                        : ""
                  }
                  open={isActive || isArchived ? undefined : false}
                >
                  <Button
                    type="primary"
                    icon={<EditOutlined />}
                    disabled={isActive || isArchived || !onEdit}
                    onClick={() => onEdit?.(task)}
                  >
                    {t("detail.edit")}
                  </Button>
                </Tooltip>

                {isActive ? (
                  <Tooltip title={t("list.deleteActiveError")}>
                    <Button danger disabled icon={<DeleteOutlined />}>
                      {t("detail.delete")}
                    </Button>
                  </Tooltip>
                ) : (
                  <Popconfirm
                    title={
                      isArchived
                        ? t("list.deleteArchivedTitle")
                        : t("list.deleteTitle")
                    }
                    description={
                      isArchived
                        ? t("list.deleteArchivedNote")
                        : t("list.deleteDraftNote")
                    }
                    okText={deleteLabel}
                    okButtonProps={{ danger: true, loading: deleting }}
                    cancelText={t("list.cancelButton")}
                    onConfirm={handleDelete}
                  >
                    <Button danger icon={<DeleteOutlined />} loading={deleting}>
                      {t("detail.delete")}
                    </Button>
                  </Popconfirm>
                )}
              </div>
            </div>
          </>
        )}
      </Modal>
    </BrandThemeProvider>
  );
}

export default TaskDetailModal;
